import { Check, Plus, X } from 'lucide-react';
import styles from './FilterMenu.module.css';
import { useMenuPopover } from './useMenuPopover.ts';

export type FilterOption = { readonly value: string; readonly label: string };

export type FilterMenuProps = {
  label: string;
  options: readonly FilterOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  clearLabel: string;
};

export const FilterMenu = ({ label, options, value, onChange, clearLabel }: FilterMenuProps) => {
  const chosen = options.find((option) => option.value === value);
  const { open, toggle, close, menuId, root, list, trigger, moveWithKeys, place } = useMenuPopover(
    options.findIndex((option) => option.value === value),
  );

  return (
    <div ref={root} className={styles['root']}>
      <button
        ref={trigger}
        type="button"
        className={styles['trigger']}
        data-active={chosen !== undefined}
        aria-label={chosen === undefined ? label : `${label}, ${chosen.label}`}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={toggle}
      >
        {chosen === undefined ? <Plus size={16} aria-hidden="true" /> : null}
        <span>{label}</span>
        {chosen === undefined ? null : <strong className={styles['chosen']}>{chosen.label}</strong>}
      </button>
      {chosen === undefined ? null : (
        <button
          type="button"
          className={styles['clear']}
          aria-label={clearLabel}
          onClick={() => {
            onChange(null);
          }}
        >
          <X size={14} aria-hidden="true" />
        </button>
      )}
      {open ? (
        <div
          ref={list}
          id={menuId}
          role="menu"
          tabIndex={-1}
          aria-label={label}
          className={styles['menu']}
          style={place}
          onKeyDown={moveWithKeys}
        >
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              role="menuitemradio"
              aria-checked={option.value === value}
              className={styles['item']}
              onClick={() => {
                onChange(option.value);
                close();
              }}
            >
              <span>{option.label}</span>
              {option.value === value ? <Check size={16} aria-hidden="true" /> : null}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};
